import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from "@angular/core"; import { Location } from "@angular/common";

import { catchError, Observable, of, shareReplay } from 'rxjs';

import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { ReponseBase } from './reponse-base';
import { Emplacement } from '../entites/emplacement';
import { ItemMin } from '../entites/item-min';
import { ItemService } from './item-service';
import { EmployeEntite } from '../entites/employe';
import { CodeProjetEntite } from '../entites/code-projet';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { ReponseFiltre } from './reponses/reponse-filter';
import { BaseService, CacheMap, Store } from './base.service';
import { RequeteMembreEmplacement } from './requetes/requete-membre-emplacement';
import { MembreEmplacement } from '../entites/membre-emplacement';
import { PrevisionMeteo } from '../entites/prevision-meteo';
import { Requisition } from '../entites/requisition';
import { EmplacementAdresseEntite } from '../entites/emplacement-adresse-entite';
import { RequeteAjouterEmplacementAdresse } from './requetes/requete-ajouter-emplacement-adresse';
import { RequeteModifierEmplacementAdresse } from './requetes/requete-modifier-emplacement-adresse';
import { ItemCommandeEntite } from '../entites/item-commande';
import { EvaluationFournisseur } from '../entites/evaluation-fournisseur';
import { Fournisseur } from '../entites/fournisseur';

@Injectable({ providedIn: 'root' })
export class EmplacementService extends BaseService {

  private modifierEmplacementUrl = environment.apiBaseUrl + 'emplacement/modifier';
  private ajouterEmplacementUrl = environment.apiBaseUrl + 'emplacement';
  private obtenirInventaireEmplacementUrl = environment.apiBaseUrl + 'emplacement/{id}/inventaire';
  private obtenirEmplacementsUrl = environment.apiBaseUrl + 'emplacement';
  private obtenirDetailEmplacementUrl = environment.apiBaseUrl + 'emplacement/{id}';
  private obtenirMembresUrl = environment.apiBaseUrl + 'emplacement/{id}/membres';
  private obtenirCodeProjetsUrl = environment.apiBaseUrl + 'emplacement/codeProjets';
  private ajouterMembreUrl = environment.apiBaseUrl + 'emplacement/{emplacementId}/membre/{employeId}';
  private modifierMembreUrl = environment.apiBaseUrl + 'emplacement/{emplacementId}/membre/{employeId}';
  private supprimerMembreUrl = environment.apiBaseUrl + 'emplacement/{emplacementId}/membre/{employeId}';
  private archiverEmplacementUrl = environment.apiBaseUrl + 'emplacement/archiver';
  private obtenirPrevisionMeteoUrl = environment.apiBaseUrl + 'emplacement/{id}/previsionmeteo';
  private obtenirRequisitionsEmplacementUrl = environment.apiBaseUrl + 'emplacement/{id}/requisitions';
  private obtenirAdressesEmplacementUrl = environment.apiBaseUrl + 'emplacement/{id}/adresses';
  private ajouterAdresseEmplacementUrl = environment.apiBaseUrl + 'emplacement/{id}/adresse';
  private modifierAdresseEmplacementUrl = environment.apiBaseUrl + 'emplacement/{id}/adresse/{emplacementAdresseId}';
  private supprimerAdresseEmplacementUrl = environment.apiBaseUrl + 'emplacement/{id}/adresse/{emplacementAdresseId}';
  private obtenirDernieresCommandesEmplacementUrl = environment.apiBaseUrl + 'emplacement/{id}/commandes';
  private obtenirEvaluationsEmplacementUrl = environment.apiBaseUrl + 'emplacement/{id}/evaluations';
  private obtenirFournisseursAvecBonsCommandeUrl = environment.apiBaseUrl + 'emplacement/{id}/fournisseurs-avec-bons-commande';

  private cacheMapInventaire = new CacheMap<string, Observable<ReponseBase<ReponseFiltre<ItemMin>>>>();

  public httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  constructor(
    public itemService: ItemService,
    public http: HttpClient,
    public router: Router,
    public messageService: MessageService
  ) {
    super(messageService, router);
  }

    public Obtenir(): Promise<ReponseBase<Emplacement[]>> {

      var p = this.http.get<ReponseBase<Emplacement[]>>(this.obtenirEmplacementsUrl)
        .toPromise();
  
        p.catch(() => this.router.navigateByUrl('error'));
  
        return p;
    }

  public ObtenirDetail(emplacementId: number): Promise<ReponseBase<Emplacement>> {

    var p = this.http.get<ReponseBase<Emplacement>>(this.obtenirDetailEmplacementUrl.replace('{id}', emplacementId.toString()))
      .toPromise();

      p.catch(() => this.router.navigateByUrl('error'));

      return p;
  }

  public ModifierEmplacement(emplacement: Emplacement): Promise<ReponseBase<Emplacement>> {
    var p = this.http.post<ReponseBase<Emplacement>>(this.modifierEmplacementUrl, JSON.stringify(emplacement), this.httpOptions).toPromise();

    p.catch(() => this.router.navigateByUrl('error'));

    return p;
  }

  public ObtenirInventaire(emplacementId: number, filtreCodeProjet: boolean = false, filters: LazyLoadEvent): Observable<ReponseBase<ReponseFiltre<ItemMin>>> {
    const cacheKey = `${emplacementId}-${filtreCodeProjet}-${JSON.stringify(filters)}`;
    
    if (!this.cacheMapInventaire.has(cacheKey)) {
      var params = this.GenerateFilterParams(filters);

      params = params.set('filtreCodeProjet', filtreCodeProjet.toString());
      
      const request$ = this.http.get<ReponseBase<ReponseFiltre<ItemMin>>>(
        this.obtenirInventaireEmplacementUrl.replace('{id}', emplacementId.toString()),
        { params }
      ).pipe(
        catchError(error => {
          this.cacheMapInventaire.delete(cacheKey);
          throw error;
        }),
        shareReplay()
      );
      
      this.cacheMapInventaire.set(cacheKey, request$);
    }

    return this.cacheMapInventaire.get(cacheKey)!;
  }

  public ObtenirMembres(emplacementId: number): Observable<MembreEmplacement[]> {
    return this.http.get<MembreEmplacement[]>(this.obtenirMembresUrl.replace('{id}', emplacementId.toString()));
  }

  public ObtenirCodeProjets(): Promise<ReponseBase<CodeProjetEntite[]>> {

    var p = this.http.get<ReponseBase<CodeProjetEntite[]>>(this.obtenirCodeProjetsUrl)
      .toPromise();

      p.catch(() => this.router.navigateByUrl('error'));

      return p;
  }

  public AjouterEmplacement(emplacement: Emplacement): Promise<ReponseBase<Emplacement>> {
    var p = this.http.post<ReponseBase<Emplacement>>(this.ajouterEmplacementUrl, JSON.stringify(emplacement), this.httpOptions).toPromise();

    p.catch(() => this.router.navigateByUrl('error'));

    return p;
  }

  public AjouterMembre(emplacementId: number, employeId: number, requeteMembre: RequeteMembreEmplacement): Observable<void> {
    return this.http.post<void>(this.ajouterMembreUrl.replace('{emplacementId}', emplacementId.toString()).replace('{employeId}', employeId.toString()), JSON.stringify(requeteMembre), this.httpOptions);
  }

  public ModifierMembre(emplacementId: number, employeId: number, requeteMembre: RequeteMembreEmplacement): Observable<void> {
    return this.http.put<void>(this.modifierMembreUrl.replace('{emplacementId}', emplacementId.toString()).replace('{employeId}', employeId.toString()), JSON.stringify(requeteMembre), this.httpOptions);
  }

  public SupprimerMembre(emplacementId: number, employeId: number): Observable<void> {
    return this.http.delete<void>(this.supprimerMembreUrl.replace('{emplacementId}', emplacementId.toString()).replace('{employeId}', employeId.toString()));
  }

  public Archiver(emplacement: Emplacement): Promise<ReponseBase<boolean>> {
    var p = this.http.post<ReponseBase<boolean>>(this.archiverEmplacementUrl, JSON.stringify(emplacement), this.httpOptions).toPromise();

    p.catch(() => this.router.navigateByUrl('error'));

    return p;
  }

  public ObtenirPrevisionMeteo(emplacementId: number, date?: Date): Observable<PrevisionMeteo[]> {
    const params = date ? { date: date.toISOString() } : {};
    return this.http.get<PrevisionMeteo[]>(this.obtenirPrevisionMeteoUrl.replace('{id}', emplacementId.toString()), { params });
  }

  public ObtenirRequisitionsEmplacement(emplacementId: number): Observable<Requisition[]> {
    return this.http.get<Requisition[]>(this.obtenirRequisitionsEmplacementUrl.replace('{id}', emplacementId.toString()))
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  public ObtenirDernieresCommandesEmplacement(emplacementId: number, max?: number): Observable<ItemCommandeEntite[]> {
    const params = max ? { max: max.toString() } : {};
    return this.http.get<ItemCommandeEntite[]>(this.obtenirDernieresCommandesEmplacementUrl.replace('{id}', emplacementId.toString()), { params })
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Obtient les évaluations fournisseur pour un emplacement donné.
   */
  public ObtenirEvaluationsParEmplacement(emplacementId: number): Observable<EvaluationFournisseur[]> {
    return this.http.get<EvaluationFournisseur[]>(this.obtenirEvaluationsEmplacementUrl.replace('{id}', emplacementId.toString()))
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Obtient les fournisseurs ayant un bon de commande pour cet emplacement.
   */
  public ObtenirFournisseursAvecBonsCommandeParEmplacement(emplacementId: number): Observable<Fournisseur[]> {
    return this.http.get<Fournisseur[]>(this.obtenirFournisseursAvecBonsCommandeUrl.replace('{id}', emplacementId.toString()))
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  public ObtenirAdressesEmplacement(emplacementId: number): Observable<EmplacementAdresseEntite[]> {
    return this.http.get<EmplacementAdresseEntite[]>(this.obtenirAdressesEmplacementUrl.replace('{id}', emplacementId.toString())).pipe(
      catchError(error => this.handleError(error))
    );
  }

  public AjouterAdresseEmplacement(emplacementId: number, requete: RequeteAjouterEmplacementAdresse): Observable<EmplacementAdresseEntite> {
    return this.http.post<EmplacementAdresseEntite>(this.ajouterAdresseEmplacementUrl.replace('{id}', emplacementId.toString()), requete).pipe(
      catchError(error => this.handleError(error))
    );
  }

  public SupprimerAdresseEmplacement(emplacementId: number, emplacementAdresseId: number): Observable<void> {
    return this.http.delete<void>(this.supprimerAdresseEmplacementUrl.replace('{id}', emplacementId.toString()).replace('{emplacementAdresseId}', emplacementAdresseId.toString())).pipe(
      catchError(error => this.handleError(error))
    );
  }
}
